import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Loader2, Plus, Lightbulb, Globe, Shield, ShoppingCart, AlertCircle } from 'lucide-react'
import { useState, useMemo, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { CustomDomainItem, useAddCustomDomain } from '@/lib/api/domains'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

/**
 * Validate domain format (both apex and subdomain)
 */
const isValidDomain = (hostname: string): boolean => {
	// Basic domain validation: at least one dot, valid characters
	const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
	return domainRegex.test(hostname);
}

/**
 * Check if domain has a subdomain (more than one level before TLD)
 */
const hasSubdomain = (hostname: string): boolean => {
	const parts = hostname.split('.');
	// e.g., checkout.example.com has 3 parts, example.com has 2
	return parts.length > 2;
}

/**
 * Extract the apex domain from a hostname
 */
const getApexDomain = (hostname: string): string => {
	const parts = hostname.split('.');
	if (parts.length <= 2) return hostname;
	return parts.slice(-2).join('.');
}

/**
 * Suggested subdomain prefixes for checkout domains
 */
const SUBDOMAIN_SUGGESTIONS = [
	{ prefix: 'checkout', icon: ShoppingCart, label: 'checkout', description: 'Most common for checkout flows' },
	{ prefix: 'secure', icon: Shield, label: 'secure', description: 'Emphasizes security' },
	{ prefix: 'pay', icon: Globe, label: 'pay', description: 'Short and clear' },
] as const;

interface DomainListHeaderProps {
	onDomainCreated?: (domainName: string) => void
	domainsList?: CustomDomainItem[]
	setSelectedDomain?: (domain: CustomDomainItem | null) => void
}

export const DomainListHeader = ({ 
	onDomainCreated,
	domainsList = [],
	setSelectedDomain,
}: DomainListHeaderProps) => {
	const { t } = useTranslation('domains')
	const [domainName, setDomainName] = useState('')
	const [dialogOpen, setDialogOpen] = useState(false)
	const [pendingDomainName, setPendingDomainName] = useState<string | null>(null)
	const { mutate: addDomain, isPending: isAddingDomain } = useAddCustomDomain()

	// Watch for newly created domain in the list and open details modal
	useEffect(() => {
		if (pendingDomainName && domainsList.length > 0) {
			const createdDomain = domainsList.find(d => d.domain === pendingDomainName)
			if (createdDomain) {
				setSelectedDomain?.(createdDomain)
				setPendingDomainName(null)
			}
		}
	}, [domainsList, pendingDomainName, setSelectedDomain])

	// Analyze the entered domain
	const domainAnalysis = useMemo(() => {
		const trimmed = domainName.trim().toLowerCase();
		const isValid = isValidDomain(trimmed);
		const isApex = isValid && !hasSubdomain(trimmed);
		const apex = isValid ? getApexDomain(trimmed) : '';
		
		return {
			trimmed,
			isValid,
			isApex,
			apex,
			suggestions: isApex ? SUBDOMAIN_SUGGESTIONS.map(s => `${s.prefix}.${trimmed}`) : [],
		};
	}, [domainName]);

	const handleAddDomain = (domain?: string) => {
		const domainToAdd = domain || domainAnalysis.trimmed;
		if (!domainToAdd || !isValidDomain(domainToAdd)) return;
		
		// Track the domain name so we can open details modal after creation
		setPendingDomainName(domainToAdd);
		
		addDomain({ domain: domainToAdd }, {
			onSuccess: () => {
				setDomainName('');
				setDialogOpen(false);
				// Notify parent to refetch and find the domain
				onDomainCreated?.(domainToAdd);
			},
			onError: () => {
				// Clear pending domain on error
				setPendingDomainName(null);
			}
		});
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && domainAnalysis.isValid && !isAddingDomain) {
			if (domainAnalysis.isApex) {
				setDialogOpen(true);
			} else {
				handleAddDomain();
			}
		}
	}

	return (
		<div className="flex flex-col gap-4">
			{/* Header */}
		<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
			<div>
				<h1 className="text-xl font-semibold sm:text-2xl">{t('title')}</h1>
				<p className="text-muted-foreground text-sm">{t('description')}</p>
			</div>
			</div>

			{/* Add Domain Card */}
			<div className="rounded-lg border bg-card p-4 shadow-sm">
				<div className="flex flex-col gap-4">
					{/* Best Practice Hint */}
					<div className="flex items-start gap-3 rounded-md bg-blue-50 dark:bg-blue-950/30 px-3 py-2.5 text-sm">
						<Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
						<div className="text-blue-800 dark:text-blue-200">
							<span className="font-medium">Best practice:</span> Use a subdomain like{' '}
							<code className="rounded bg-blue-100 dark:bg-blue-900/50 px-1.5 py-0.5 font-mono text-xs">checkout.yourdomain.com</code>{' '}
							or{' '}
							<code className="rounded bg-blue-100 dark:bg-blue-900/50 px-1.5 py-0.5 font-mono text-xs">secure.yourdomain.com</code>{' '}
							for your checkout pages.
						</div>
					</div>

					{/* Input Row */}
					<div className="flex flex-col sm:flex-row gap-3">
						<div className="flex-1 space-y-1.5">
							<div className="relative">
								<Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
				<Input
					value={domainName}
									onChange={(e) => setDomainName(e.target.value.toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, ''))}
									onKeyDown={handleKeyDown}
									placeholder="checkout.yourdomain.com"
									className="pl-9 h-10"
								/>
							</div>
							{/* Validation feedback */}
							{domainName && !domainAnalysis.isValid && (
								<p className="text-xs text-destructive flex items-center gap-1">
									<AlertCircle className="h-3 w-3" />
									Enter a valid domain (e.g., checkout.example.com)
								</p>
							)}
						</div>

						{/* Add Button or Dialog Trigger */}
						{domainAnalysis.isApex && domainAnalysis.isValid ? (
							<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
								<DialogTrigger asChild>
									<Button 
										disabled={!domainAnalysis.isValid || isAddingDomain}
										className="w-full sm:w-auto h-10"
									>
										{isAddingDomain ? <Loader2 className="size-4 animate-spin mr-1.5" /> : <Plus className="size-4 mr-1.5" />}
										{t('addDomain')}
									</Button>
								</DialogTrigger>
								<DialogContent className="sm:max-w-[480px]">
									<DialogHeader>
										<DialogTitle className="flex items-center gap-2">
											<Globe className="h-5 w-5" />
											Add Domain
										</DialogTitle>
										<DialogDescription>
											You entered an apex domain. We recommend using a subdomain for better organization.
										</DialogDescription>
									</DialogHeader>

									<div className="py-4 space-y-4">
										{/* Current domain */}
										<div className="rounded-lg border p-3 bg-muted/50">
											<div className="text-xs text-muted-foreground mb-1">You entered:</div>
											<div className="font-mono text-sm font-medium">{domainAnalysis.trimmed}</div>
										</div>

										{/* Suggestions */}
										<div className="space-y-2">
											<div className="text-sm font-medium">Recommended subdomains:</div>
											<div className="grid gap-2">
												{SUBDOMAIN_SUGGESTIONS.map((suggestion) => {
													const fullDomain = `${suggestion.prefix}.${domainAnalysis.trimmed}`;
													const Icon = suggestion.icon;
													return (
														<button
															key={suggestion.prefix}
															onClick={() => handleAddDomain(fullDomain)}
															disabled={isAddingDomain}
															className={cn(
																"flex items-center gap-3 rounded-lg border p-3 text-left transition-all",
																"hover:border-primary hover:bg-primary/5",
																"focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
																isAddingDomain && "opacity-50 cursor-not-allowed"
															)}
														>
															<div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
																<Icon className="h-4 w-4" />
															</div>
															<div className="flex-1 min-w-0">
																<div className="font-mono text-sm font-medium truncate">{fullDomain}</div>
																<div className="text-xs text-muted-foreground">{suggestion.description}</div>
															</div>
															<Badge variant="secondary" className="text-[10px]">Recommended</Badge>
														</button>
													);
												})}
											</div>
										</div>

										{/* Divider */}
										<div className="relative">
											<div className="absolute inset-0 flex items-center">
												<span className="w-full border-t" />
											</div>
											<div className="relative flex justify-center text-xs uppercase">
												<span className="bg-background px-2 text-muted-foreground">or</span>
											</div>
										</div>

										{/* Use apex anyway */}
										<Button
											variant="outline"
											className="w-full"
											onClick={() => handleAddDomain(domainAnalysis.trimmed)}
											disabled={isAddingDomain}
										>
											{isAddingDomain ? (
												<Loader2 className="size-4 animate-spin mr-1.5" />
											) : null}
											Use {domainAnalysis.trimmed} anyway
										</Button>
									</div>
								</DialogContent>
							</Dialog>
						) : (
				<Button 
								disabled={!domainAnalysis.isValid || isAddingDomain} 
								onClick={() => handleAddDomain()}
								className="w-full sm:w-auto h-10"
				>
								{isAddingDomain ? <Loader2 className="size-4 animate-spin mr-1.5" /> : <Plus className="size-4 mr-1.5" />}
								{t('addDomain')}
				</Button>
						)}
					</div>

					{/* Quick suggestions when typing apex domain */}
					{domainAnalysis.isApex && domainAnalysis.isValid && !dialogOpen && (
						<div className="flex flex-wrap items-center gap-2 text-xs">
							<span className="text-muted-foreground">Quick add:</span>
							{SUBDOMAIN_SUGGESTIONS.slice(0, 2).map((suggestion) => {
								const fullDomain = `${suggestion.prefix}.${domainAnalysis.trimmed}`;
								return (
									<button
										key={suggestion.prefix}
										onClick={() => handleAddDomain(fullDomain)}
										disabled={isAddingDomain}
										className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono text-xs transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary disabled:opacity-50"
									>
										{fullDomain}
									</button>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

